import React, { memo } from "react";
import PropTypes from "prop-types";
import ListItemText from "@mui/material/ListItemText";
import { getThumbnailPath } from "~/libs/image";
import ScrollIntoViewIfNeeded from "~/components/ScrollIntoViewIfNeeded";
import { equals } from "ramda";

import ListItemButton from "@mui/material/ListItemButton";
import { styled } from "@mui/material/styles";

const StyledListItemText = styled(ListItemText)(() => ({
  margin: 0,
  "&.MuiListItemText-primary": {
    variant: "body1"
  }
}));

const StyledListItemButton = styled(ListItemButton)(props => ({
  padding: props.theme.spacing(2, 4),
  flexDirection: "column",
  cursor: props.isActive ? "default" : "pointer",
  pointerEvents: props.isActive ? "none" : "auto"
}));

const Wrapper = styled("div")(({ theme }) => ({
  width: "100%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  marginBottom: theme.spacing(1.5)
}));

const Ratio = styled("div")(props => ({
  position: "relative",
  width: "35%",
  height: "auto",
  border: "1px solid black",
  "& + &": {
    marginLeft: -1
  },
  "&::after": {
    content: '""',
    display: "block",
    height: 0,
    width: "100%",
    paddingBottom: `${props.aspectRatio}%`
  },
  "& > *": {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    display: "block"
  }
}));

function SinglePagePreview({ page, label, isActive, onClick, aspectRatio }) {
  return (
    <ScrollIntoViewIfNeeded active={isActive}>
      <StyledListItemButton onClick={onClick} selected={isActive}>
        <Wrapper>
          <Ratio aspectRatio={aspectRatio}>
            <img className="lazyload" data-sizes="auto" data-src={getThumbnailPath(page)} alt={label} />
          </Ratio>
        </Wrapper>
        <StyledListItemText primary={label} />
      </StyledListItemButton>
    </ScrollIntoViewIfNeeded>
  );
}

SinglePagePreview.whyDidYouRender = false;

SinglePagePreview.propTypes = {
  page: PropTypes.number,
  label: PropTypes.string,
  isActive: PropTypes.bool,
  onClick: PropTypes.func,
  aspectRatio: PropTypes.number
};

function areEqual(prevProps, nextProps) {
  return (
    equals(prevProps.aspectRatio, nextProps.aspectRatio) &&
    equals(prevProps.isActive, nextProps.isActive) &&
    equals(prevProps.label, nextProps.label) &&
    equals(prevProps.page, nextProps.page)
  );
}

const Memoized = memo(SinglePagePreview, areEqual);

export default Memoized;
